import { GridColDef } from '@mui/x-data-grid';
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { atom, useSetAtom } from 'jotai';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { COLORS } from '../../../theme/themeConstants';
import { useCallback, useMemo, useState,useEffect } from 'react';
import EditStoreOwnerModal from '../EditStoreOwnerModal';
import React from 'react';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import {
  useBlockUnblockFaqs,
  useDeleteFaqs,
  useGetAllFaqs,
} from '../../Query/FaqQuery';
import AddFaqs from './AddFaqs.tsx';
import { useManageWallet } from '../ManageWallet/useManageWallet.ts';
import CommonDataGrid from '../../Stateless/CommonDataGrid.tsx';
import ErrorHandler from '../../Stateless/Common/ErrorHandler.tsx';
import { useDebounce } from '../Utils/useDounce.tsx';
import { usePermissions } from '../../../context/PermissionContext';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PermissionActionButtons from '../../Stateless/Common/PermissionActionButtons.tsx';

export interface DeliveryManagerFormValues {
  id?: string;
  question: string;
  questionInArabic: string;
  answer: string;
  answerInArabic: string;
}

export const deliveryManagerAtom = atom<DeliveryManagerFormValues | null>(null);

type faqWithSno = {
  id: string;
  name: string;
  status: string;
  sno: number;
};


const FaqTable = () => {
  const { showMessage } = useSnackbar();
  const setDeliveryManagerAtom = useSetAtom(deliveryManagerAtom);
  const { hasPermission } = usePermissions();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [modalContent] = useState<'add' | 'edit'>('edit');

  const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 400);

  const { params, dispatch } = useManageWallet();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
  const { data: axiosResponse, isLoading, refetch } = useGetAllFaqs(memoizedParams);

  const FaqData= axiosResponse?.data

  const { mutate: blockUnblockFaq } = useBlockUnblockFaqs();

  const { mutate: deleteFaqs } = useDeleteFaqs();


   const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value)
    };
  
    useEffect(() => {
      dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
    }, [debouncedSearch]);


  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };



  const filteredRows = React.useMemo(() => {
    const faqList = FaqData?.data || [];

    // Add sno field to each row and ensure all required fields are present
    return faqList?.map((row, index) => {
      // Ensure we have a valid row object
      if (!row) return { id: `empty-${index}` } as faqWithSno;

      // Calculate S.No based on current page and page size
      const sno = index + 1 + (Number(params.page - 1)) * Number(params.limit)
      // Create a new object with all the row properties plus sno
      const rowWithSno = {
        ...row,
        id: row.faqId,
        name: row.question,
        status: row.status,
        sno: sno,
      };

      return rowWithSno as faqWithSno;
    });
  }, [params.search, FaqData, params.page, params.limit]);

  const handleBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one Faq to block', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to block?');
      if (!confirmed) return;

      blockUnblockFaq({
        id: selectedRows,
        data: { status: 'blocked' },
      });
    }
  };

  const handleUnBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one Faq to unblock', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to unblock?');
      if (!confirmed) return;

      blockUnblockFaq({
        id: selectedRows,
        data: { status: 'unblocked' },
      });
    }
  };

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage(
        'Please select at least one Delivery manager to delete',
        'error'
      );
    } else {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      // deleteFaqs(selectedRows);
      deleteFaqs(selectedRows, {
      onSuccess: () => {
        setSelectedRows([]);
        showMessage('FAQ deleted successfully', 'success');

        // Refetch data to update the table
        refetch();
      },
      onError: () => {
        showMessage('Error deleting FAQ', 'error');
        // If there's an error, we might want to restore the selection
        // setSelectedRows(selectedRows); // Uncomment if you want to restore selection on error
      },
      });
      setSelectedRows([])
    }
  }, [selectedRows,showMessage]);

  const handleBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to block this Faq?'
    );
    if (!confirmed) return;

    await blockUnblockFaq({
      id: [id],
      data: { status: 'blocked' },
    });
  };
  const handleUnBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to unblock this Faq?'
    );
    if (!confirmed) return;

    await blockUnblockFaq({
      id: [id],
      data: { status: 'unblocked' },
    });
  };

  const handleDeleteButton = useCallback(
    (id: string) => {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;
      console.log(id);

      deleteFaqs([id]);
    },
    [selectedRows]
  );

  const handleAddSubAdmin = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleEditClick = (row: any) => {
    const faqData = {
      id: row.id,
      question: row.question,
      questionInArabic: row.questionInArabic,
      answer: row.answer,
      answerInArabic: row.answerInArabic,
    };
    setDeliveryManagerAtom(faqData);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setDeliveryManagerAtom(null);
  };

  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = FaqData?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.faqId), String(item.status || '')])
    );
    const statuses = selectedRows.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    return { showBlock: hasUnblocked, showUnblock: hasBlocked };
  }, [selectedRows, FaqData]);

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const canEdit = hasPermission('faq', 'Edit');
    const canDelete = hasPermission('faq', 'Delete');
    if (!canEdit && !canDelete) return null;
    const id = typeof row.id === 'string' ? row.id : '';
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          {canEdit && (
            <MenuItem onClick={() => { setAnchorEl(null); handleEditClick(row); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={() => { setAnchorEl(null); if (id) handleDeleteButton(id); }}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'sno',
      headerName: 'S.No',
    },
    {
      field: 'question',
      headerName: 'Question',
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const isBlocked = params.value === 'blocked';
        const id = params.row.id as string;
        const canBlock = hasPermission('faq', 'Block');
        const canUnblock = hasPermission('faq', 'Unblock');
        if ((isBlocked && !canUnblock) || (!isBlocked && !canBlock)) return null;
        return (
          <IconButton size="small" sx={{ color: isBlocked ? '#f44336' : '#4caf50' }} onClick={() => isBlocked ? handleUnBlockClickButton(id) : handleBlockClickButton(id)}>
            {isBlocked ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
          </IconButton>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => <RowActions row={params.row} />,
    },
  ];

  return (
    <>
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          color: '#666',
        }}
      >
        <Box
          sx={{
            // p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2, // Optional: adds spacing between wrapped items
          }}
        >
          {/* Search - Left */}

          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search Faqs"
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none', // remove inner border
                },
                '& fieldset': {
                  border: 'none', // remove Material-UI default border
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px', // adjust this value to move the placeholder lower
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Action Buttons + Add Button - Right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <PermissionActionButtons
              module="faq"
              onBlock={showBlock ? handleBlockClick : undefined}
              onUnblock={showUnblock ? handleUnBlockClick : undefined}
              onDelete={selectedRows.length ? handleDeleteClick : undefined}
            />
            {hasPermission('faq', 'Add') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSubAdmin}
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  bgcolor: COLORS.primary.main,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: COLORS.primary.main,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                Add FAQ
              </Button>
            )}
          </Box>
        </Box>

        <EditStoreOwnerModal
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          title="Add FAQ"
        >
          <AddFaqs onCancel={handleCloseAddDialog} />
        </EditStoreOwnerModal>

        <EditStoreOwnerModal
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          title={modalContent === 'add' ? 'Add FAQ' : 'Edit FAQ'}
        >
          <AddFaqs onCancel={handleCloseEditDialog} />
        </EditStoreOwnerModal>

        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={filteredRows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={FaqData?.pagination?.totalItems}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message="No categories found" onRetry={() => refetch()} />
          }}
          onRowSelectionModelChange={(ids: string[]) => setSelectedRows(ids)}
          selectionModel={selectedRows}
        />
      </Box>
    </>
  );
};

export default FaqTable;
